<template>
	<!-- Recorta el logo de la actividad a 950 × 400 px (el recuadro del RIDE, 9,5 × 4 cm). Canvas nativo, sin dependencias:
	la geometría vive en service/logoCrop.js. Se puede alejar hasta que el logo entre entero
	(lo que sobra queda transparente) o acercar para recortarlo. -->
	<Dialog :visible="visible" header="Ajustar logotipo" :modal="true" :style="{width: '42rem'}"
		:breakpoints="{'640px': '96vw'}" @update:visible="onVisibility">
		<p class="cropper-desc">Arrastra la imagen para moverla y usa el control para acercarla o alejarla. Se guarda en 950 × 400 px, el tamaño del logo en tus comprobantes; lo que no cubra el logo queda transparente.</p>

		<div ref="frame" class="cropper-frame" :class="{'is-dragging': dragging}" tabindex="0" role="img"
			aria-label="Vista previa del logotipo. Flechas para mover, más y menos para acercar o alejar."
			@pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="onPointerUp" @pointercancel="onPointerUp"
			@wheel.prevent="onWheel" @keydown="onKeydown">
			<canvas ref="canvas" :width="logoWidth" :height="logoHeight"></canvas>
			<div v-if="!image && !loadError" class="cropper-state">Abriendo la imagen…</div>
			<div v-if="loadError" class="cropper-state cropper-state-error" role="alert">{{ loadError }}</div>
		</div>

		<div class="zoom-row">
			<Button icon="pi pi-minus" class="p-button-rounded p-button-text p-button-sm" aria-label="Alejar" :disabled="!image" @click="zoomBy(1 / 1.15)" />
			<Slider v-model="zoomPosition" :min="0" :max="1000" class="zoom-slider" :disabled="!image || !canZoom" aria-label="Acercar o alejar" />
			<Button icon="pi pi-plus" class="p-button-rounded p-button-text p-button-sm" aria-label="Acercar" :disabled="!image" @click="zoomBy(1.15)" />
		</div>
		<div class="fit-row">
			<Button label="Logo entero" icon="pi pi-window-minimize" class="p-button-outlined p-button-sm" :disabled="!image" @click="fitContain" />
			<Button label="Llenar el recuadro" icon="pi pi-window-maximize" class="p-button-outlined p-button-sm" :disabled="!image" @click="fitCover" />
		</div>

		<template #footer>
			<Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="$emit('cancel')" />
			<Button label="Usar este logo" icon="pi pi-check" class="p-button-success" :loading="exporting" :disabled="!image" @click="confirm" />
		</template>
	</Dialog>
</template>

<script>
	import {
		centered, clamp, containScale, coverScale, coversFrame, croppedName, outputType, pan, scaleLimits, zoomTo,
		LOGO_HEIGHT, LOGO_WIDTH,
	} from '../../service/logoCrop';
	import { IMAGE_MAX_BYTES } from '../../service/imageCompression';

	const STEP = 10;  // px del logo por flecha (Shift: x5)

	function canvasToBlob(canvas, type, quality) {
		return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
	}

	export default {
		name: 'LogoCropper',
		props: {
			visible: { type: Boolean, default: false },
			file: { type: Object, default: null },  // File elegido por el usuario
		},
		emits: ['cropped', 'cancel'],
		data() {
			return {
				logoWidth: LOGO_WIDTH,
				logoHeight: LOGO_HEIGHT,
				image: null,
				objectUrl: null,
				loadedFile: null,    // el archivo que ya se abrió (o se está abriendo)
				pendingImage: null,  // solo la última carga cuenta
				state: { scale: 1, x: 0, y: 0 },
				dragging: false,
				lastPointer: null,
				loadError: null,
				exporting: false,
			}
		},
		computed: {
			limits() {
				return this.image ? scaleLimits(this.image.naturalWidth, this.image.naturalHeight) : { min: 1, max: 1 };
			},
			canZoom() {
				return this.limits.max > this.limits.min;
			},
			// El control es logarítmico: cada tramo acerca lo mismo, sea cual sea el tamaño de la imagen.
			zoomPosition: {
				get() {
					if (!this.canZoom) { return 0 }
					return Math.round(1000 * Math.log(this.state.scale / this.limits.min) / Math.log(this.limits.max / this.limits.min));
				},
				set(position) {
					if (!this.canZoom) { return }
					const scale = this.limits.min * Math.pow(this.limits.max / this.limits.min, position / 1000);
					this.update(zoomTo(this.state, scale, this.image.naturalWidth, this.image.naturalHeight));
				},
			},
		},
		watch: {
			visible(value) {
				if (value) { this.load() } else { this.release() }
			},
			file() {
				if (this.visible) { this.load() }
			},
		},
		beforeUnmount() {
			this.release();
		},
		methods: {
			onVisibility(value) {
				if (!value) { this.$emit('cancel') }
			},
			// Al abrir cambian a la vez el archivo y la visibilidad: se abre una sola vez, y una carga
			// vieja (cuya URL ya se revocó) no pisa a la nueva con un error falso.
			load() {
				if (this.file && this.file === this.loadedFile) { return }
				this.release();
				this.loadError = null;
				if (!this.file) { return }
				this.loadedFile = this.file;
				this.objectUrl = URL.createObjectURL(this.file);
				const image = new Image();
				this.pendingImage = image;
				image.onload = () => {
					if (this.pendingImage !== image) { return }
					this.image = image;
					// Por defecto, el logo entero: los logos no se recortan sin que el usuario lo pida.
					this.update(centered(containScale(image.naturalWidth, image.naturalHeight), image.naturalWidth, image.naturalHeight));
					this.$nextTick(() => { if (this.$refs.frame) { this.$refs.frame.focus({preventScroll: true}) } });
				};
				image.onerror = () => {
					if (this.pendingImage !== image) { return }
					this.loadError = 'No se pudo abrir esta imagen. Usa un PNG o un JPG.';
				};
				image.src = this.objectUrl;
			},
			release() {
				if (this.objectUrl) { URL.revokeObjectURL(this.objectUrl) }
				this.objectUrl = null;
				this.image = null;
				this.loadedFile = null;
				this.pendingImage = null;
				this.dragging = false;
			},
			update(state) {
				this.state = state;
				this.$nextTick(this.draw);
			},
			draw() {
				const canvas = this.$refs.canvas;
				if (!canvas || !this.image) { return }
				const ctx = canvas.getContext('2d');
				ctx.clearRect(0, 0, LOGO_WIDTH, LOGO_HEIGHT);
				ctx.imageSmoothingQuality = 'high';
				ctx.drawImage(this.image, this.state.x, this.state.y,
					this.image.naturalWidth * this.state.scale, this.image.naturalHeight * this.state.scale);
			},
			// Píxeles de pantalla -> píxeles del logo final.
			toLogo(pixels) {
				const width = this.$refs.frame ? this.$refs.frame.clientWidth : LOGO_WIDTH;
				return pixels * LOGO_WIDTH / (width || LOGO_WIDTH);
			},
			fitContain() {
				const { naturalWidth: w, naturalHeight: h } = this.image;
				this.update(centered(containScale(w, h), w, h));
			},
			fitCover() {
				const { naturalWidth: w, naturalHeight: h } = this.image;
				this.update(centered(coverScale(w, h), w, h));
			},
			zoomBy(factor, ax, ay) {
				if (!this.image) { return }
				const { naturalWidth: w, naturalHeight: h } = this.image;
				this.update(zoomTo(this.state, this.state.scale * factor, w, h, ax, ay));
			},
			onPointerDown(event) {
				if (!this.image || event.button > 0) { return }
				this.dragging = true;
				this.lastPointer = { x: event.clientX, y: event.clientY };
				event.currentTarget.setPointerCapture(event.pointerId);
			},
			onPointerMove(event) {
				if (!this.dragging) { return }
				const dx = this.toLogo(event.clientX - this.lastPointer.x);
				const dy = this.toLogo(event.clientY - this.lastPointer.y);
				this.lastPointer = { x: event.clientX, y: event.clientY };
				this.update(pan(this.state, dx, dy, this.image.naturalWidth, this.image.naturalHeight));
			},
			onPointerUp() {
				this.dragging = false;
			},
			onWheel(event) {
				if (!this.image) { return }
				const rect = this.$refs.frame.getBoundingClientRect();
				this.zoomBy(Math.exp(-event.deltaY * 0.0015),
					this.toLogo(event.clientX - rect.left), this.toLogo(event.clientY - rect.top));
			},
			onKeydown(event) {
				if (!this.image) { return }
				const step = event.shiftKey ? STEP * 5 : STEP;
				const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
				if (moves[event.key]) {
					event.preventDefault();
					const [dx, dy] = moves[event.key];
					this.update(pan(this.state, dx, dy, this.image.naturalWidth, this.image.naturalHeight));
				} else if (event.key === '+' || event.key === '=') {
					event.preventDefault();
					this.zoomBy(1.15);
				} else if (event.key === '-') {
					event.preventDefault();
					this.zoomBy(1 / 1.15);
				}
			},
			async confirm() {
				if (!this.image) { return }
				this.exporting = true;
				const { naturalWidth: w, naturalHeight: h } = this.image;
				const state = clamp(this.state, w, h);
				let type = outputType(this.file.type, coversFrame(state, w, h));
				let blob = await this.render(state, type);
				// Una foto con márgenes en PNG puede pasar del límite: JPEG con fondo blanco.
				if (blob && blob.size > IMAGE_MAX_BYTES && type === 'image/png') {
					type = 'image/jpeg';
					blob = await this.render(state, type);
				}
				this.exporting = false;
				if (!blob) {
					this.loadError = 'No se pudo preparar el logotipo. Intenta con otra imagen.';
					return;
				}
				this.$emit('cropped', new File([blob], croppedName(this.file.name, type), { type }));
			},
			render(state, type) {
				const canvas = document.createElement('canvas');
				canvas.width = LOGO_WIDTH;
				canvas.height = LOGO_HEIGHT;
				const ctx = canvas.getContext('2d');
				if (type === 'image/jpeg') {
					ctx.fillStyle = '#ffffff';
					ctx.fillRect(0, 0, LOGO_WIDTH, LOGO_HEIGHT);
				}
				ctx.imageSmoothingQuality = 'high';
				ctx.drawImage(this.image, state.x, state.y, this.image.naturalWidth * state.scale, this.image.naturalHeight * state.scale);
				return canvasToBlob(canvas, type, 0.92);
			},
		},
	}
</script>

<style scoped>
	.cropper-desc {
		margin: 0 0 1rem;
		line-height: 1.5;
		color: var(--text-color-secondary);
	}
	/* Damero: deja ver qué queda transparente. */
	.cropper-frame {
		position: relative;
		width: 100%;
		aspect-ratio: 950 / 400;
		border: 1px solid var(--surface-border);
		border-radius: 8px;
		overflow: hidden;
		cursor: grab;
		touch-action: none;
		background-color: #ffffff;
		background-image:
			linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
			linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
			linear-gradient(-45deg, transparent 75%, #e5e7eb 75%);
		background-size: 20px 20px;
		background-position: 0 0, 0 10px, 10px -10px, -10px 0;
	}
	.cropper-frame.is-dragging { cursor: grabbing; }
	.cropper-frame:focus-visible {
		outline: 2px solid var(--primary-color);
		outline-offset: 2px;
	}
	.cropper-frame canvas {
		display: block;
		width: 100%;
		height: 100%;
	}
	.cropper-state {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		text-align: center;
		color: var(--text-color-secondary);
		background: rgba(255, 255, 255, 0.85);
	}
	.cropper-state-error { color: #B91C1C; font-weight: 500; }
	.zoom-row {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		margin-top: 1rem;
	}
	.zoom-slider { flex: 1; }
	.fit-row {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		margin-top: 0.75rem;
	}
</style>
